import axiosClient, { ExtendedAxiosRequestConfig } from '@/common/AxiosClient';
import { EventBus } from '@/common/EventBus';
import { CurrentUser } from '@/data/users/CurrentUser';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

type AvatarQueryResult = { status: 'ready'; blob: Blob } | { status: 'processing' };

export interface CurrentUserAvatarState {
  /** Object URL to render, or null to show the initials fallback (loading/processing/none/error). */
  imageUrl: string | null;
  status: 'loading' | 'processing' | 'ready' | 'none';
}

const avatarQueryKey = (employeeId?: string) => ['avatar', employeeId] as const;

// ~3s poll for the first ~2 min (40 polls) while the scan is pending, then back off to 30s so a slow
// PROD scan doesn't hammer the verdict lookup.
const FAST_POLL_MS = 3000;
const SLOW_POLL_MS = 30000;
const FAST_POLL_LIMIT = 40;

/**
 * Loads the signed-in user's OWN avatar via an axios (CORS) request rather than a raw `<img>` src,
 * then renders it from a same-origin blob object URL. This sidesteps two problems that leave the
 * drawer avatar blank after an upload:
 *
 *  - the cross-origin ORB block (a raw API URL in `<img>` is opaque and gets blocked), and
 *  - the malware-scan quarantine: right after upload `GET /Profile/Avatar` returns **202** until
 *    Defender tags the blob clean. We poll on 202 and swap in the image on 200. Every other non-200
 *    (400 no-picture / 410 malicious / 5xx) is terminal → initials, with no error snackbar.
 *
 * Only for the current user (the only avatar that can be "processing"); everyone else keeps the
 * plain `<ProfilePicture>`.
 */
export function useCurrentUserAvatar(user: CurrentUser | null | undefined): CurrentUserAvatarState {
  const employeeId = user?.employeeId;
  const queryClient = useQueryClient();

  // `hasProfilePicture` is a mutated (non-reactive) field on the shared user object. When it flips
  // true on a first-ever upload, this nonce — bumped by the profilePictureChanged event — forces a
  // re-render so the query's `enabled` re-evaluates and the drawer picks the new avatar up.
  const [, setNonce] = useState(0);

  const enabled = !!employeeId && !!user?.hasProfilePicture;
  const pollCountRef = useRef(0);

  const query = useQuery<AvatarQueryResult>({
    queryKey: avatarQueryKey(employeeId),
    enabled,
    // A single shared query dedupes both instances (drawer + My account) into one poll + one blob.
    retry: false,
    refetchInterval: (q) => {
      const data = q.state.data;
      if (data?.status !== 'processing') {
        pollCountRef.current = 0;
        return false;
      }
      pollCountRef.current += 1;
      return pollCountRef.current <= FAST_POLL_LIMIT ? FAST_POLL_MS : SLOW_POLL_MS;
    },
    // Recover a slow scan when the user returns to the tab — but never re-probe a ready/terminal state.
    refetchOnWindowFocus: (q) => q.state.data?.status === 'processing',
    queryFn: async ({ signal }) => {
      const config: ExtendedAxiosRequestConfig = {
        params: { employeeId },
        responseType: 'blob',
        signal,
        // 410/400 are handled quietly as "no avatar" — don't pop the global error snackbar.
        showSnackbar: false,
        // The server sends no ETag/Cache-Control, so force a fresh body — otherwise a replaced avatar
        // is served stale from the HTTP cache after an upload (the original bug's other half).
        headers: { 'Cache-Control': 'no-cache' },
        // 200 = image; 202 = scan pending (keep polling). Anything else throws → terminal.
        validateStatus: (s) => s === 200 || s === 202,
      };
      const res = await axiosClient.get<Blob>('/Profile/Avatar', config);
      if (res.status === 202) return { status: 'processing' };
      return { status: 'ready', blob: res.data };
    },
  });

  // Re-probe after an upload. Subscribe on `document` directly (matches EventBus.dispatch) with a
  // removable handler; bump the nonce so a disabled query re-enables once hasProfilePicture flips.
  useEffect(() => {
    const handler = () => {
      setNonce((n) => n + 1);
      if (employeeId) queryClient.invalidateQueries({ queryKey: avatarQueryKey(employeeId) });
    };
    document.addEventListener(EventBus.event.profilePictureChanged, handler);
    return () => document.removeEventListener(EventBus.event.profilePictureChanged, handler);
  }, [employeeId, queryClient]);

  // Own the object URL PER instance (derive it from the shared blob), so one instance unmounting
  // revokes only its own URL and can't invalidate a sibling's live <img> src.
  const blob = query.data?.status === 'ready' ? query.data.blob : null;
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setImageUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  let status: CurrentUserAvatarState['status'] = 'none';
  if (enabled) {
    if (query.data?.status === 'ready' && imageUrl) status = 'ready';
    else if (query.data?.status === 'processing') status = 'processing';
    else if (query.isPending) status = 'loading';
  }

  return { imageUrl: status === 'ready' ? imageUrl : null, status };
}
